import api from './axiosInstance';
import { getApiResponseMessage, getRequestEndpoint } from '../utils/apiErrorUtils';
import logger from '../utils/logger';



/**
 * Common function to call API endpoints with comprehensive error handling.
 * @param {Object} config - API call configuration
 * @param {string} config.url - The API endpoint URL
 * @param {string} [config.method='GET'] - HTTP method (GET, POST, PUT, DELETE, PATCH)
 * @param {Object|null} [config.data=null] - Request body for POST/PUT/PATCH
 * @param {Object|null} [config.params=null] - Query parameters for GET requests
 * @param {Object} [config.headers={}] - Additional headers
 * @returns {Promise<any>} The response data from the API
 * @throws {Object} Structured error object with type, message, and details
 * @example
 * // GET request
 * const data = await apiCall({ url: '/api/users' });
 * 
 * // POST request
 * const result = await apiCall({
 *   url: '/api/users',
 *   method: 'POST',
 *   data: { name: 'John', email: 'john@example.com' }
 * });
 */
export async function apiCall({
    url,
    method = 'GET',
    data = null,
    params = null,
    headers = {},
    preserveAuthSessionOn401 = false,
}) {
    try {
        const response = await api({
            url,
            method,
            data,
            params,
            headers,
            preserveAuthSessionOn401,
        });
        return response.data;
    } catch (error) {
        // Categorize error for UI feedback
        let errorType = 'unknown';
        let errorMessage = 'An unexpected error occurred.';
        if (error.response) {
            const status = error.response.status;
            const backendMessage = getApiResponseMessage(error.response.data);
            const statusDetails = {
                400: ['validation', 'The uploaded file or request is invalid.'],
                401: ['unauthenticated', 'Please sign in to continue.'],
                403: ['forbidden', 'You do not have permission to perform this action, or this feature is unavailable.'],
                404: ['not_found', 'The requested file or document was not found.'],
                413: ['payload_too_large', 'The uploaded file or request is too large.'],
                415: ['unsupported_media_type', 'The file type or file contents are not supported.'],
            }[status];

            errorType = statusDetails?.[0] || 'server';
            errorMessage = backendMessage || statusDetails?.[1] || 'Server error occurred.';
        } else if (error.request) {
            errorType = 'network';
            errorMessage = 'Network error. Please check your connection.';
        }
        const endpoint = getRequestEndpoint(error) || url;
        logger.error('API Error:', { errorType, status: error.response?.status, endpoint, message: errorMessage });
        // Throw Error object with metadata to satisfy lint rules
        const apiError = new Error(errorMessage);
        apiError.type = errorType;
        apiError.response = error.response; // Preserve response for detailed error checking
        apiError.endpoint = endpoint;
        apiError.details = error;
        throw apiError;
    }
}
